import { useId, useState } from 'react';
import type { TabelaSchematu } from '@/lib/baza-api';
import { useI18n } from '@/lib/i18n';
import { podpowiedzi, type Podpowiedz } from '@/lib/podpowiedzi-sql';
import { cn } from '@/lib/utils';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  EDYTOR SQL Z PODPOWIADANIEM NAZW — „Działania na bazie" (29.09.2026).
 *
 *  ⚠️ KLAWISZE JAK W SSMS: F5 albo Ctrl+Enter wykonuje, Ctrl+Spacja otwiera listę także bez
 *  litery, Tab albo Enter wstawia podświetloną nazwę, Esc zamyka. Enter bez otwartej listy to
 *  zwykła nowa linia — lista nie może zjadać klawisza, którym człowiek po prostu pisze.
 *
 *  ⚠️ ZWYKŁE POLE TEKSTOWE, NIE CUDZY EDYTOR KODU. Lista stoi pod kursorem (lustrzany `div`
 *  liczy jego położenie), reguły podpowiedzi są czystą funkcją w `podpowiedzi-sql.ts`.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */

type Lista = { od: number; do: number; pozycje: Podpowiedz[]; wybrana: number; x: number; y: number };

/** Szerokość listy w pikselach — ta sama, co `w-72`; lista nie może wyjść za prawą krawędź pola. */
const SZEROKOSC_LISTY = 288;

/** Położenie kursora w polu w pikselach — lustrzany `div` z tym samym krojem, odstępami i zawijaniem. */
function polozenieKursora(el: HTMLTextAreaElement, kursor: number): { x: number; y: number } {
  const styl = getComputedStyle(el);
  const lustro = document.createElement('div');
  const cechy = [
    'boxSizing', 'width', 'fontFamily', 'fontSize', 'fontWeight', 'fontStyle', 'letterSpacing', 'lineHeight',
    'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft', 'borderTopWidth', 'borderRightWidth',
    'borderBottomWidth', 'borderLeftWidth', 'borderStyle', 'tabSize', 'textIndent', 'textTransform', 'wordSpacing',
  ] as const;
  for (const c of cechy) lustro.style[c] = styl[c];
  lustro.style.position = 'absolute';
  lustro.style.visibility = 'hidden';
  lustro.style.whiteSpace = 'pre-wrap';
  lustro.style.overflowWrap = 'break-word';
  lustro.style.top = '0';
  lustro.style.left = '-9999px';
  lustro.textContent = el.value.slice(0, kursor);
  const znacznik = document.createElement('span');
  znacznik.textContent = '​';
  lustro.appendChild(znacznik);
  document.body.appendChild(lustro);
  const wiersz = Number.parseFloat(styl.lineHeight) || Number.parseFloat(styl.fontSize) * 1.25;
  const x = znacznik.offsetLeft - el.scrollLeft;
  const y = znacznik.offsetTop - el.scrollTop + wiersz;
  lustro.remove();
  return { x, y };
}

export function EdytorSql({
  value,
  onChange,
  onWykonaj,
  tabele,
  placeholder,
  poleRef,
}: {
  value: string;
  onChange: (v: string) => void;
  onWykonaj: () => void;
  tabele: readonly TabelaSchematu[];
  placeholder: string;
  /** Pole z zewnątrz — drzewo tabel wstawia kliknięte nazwy w miejscu kursora. */
  poleRef: React.RefObject<HTMLTextAreaElement | null>;
}) {
  const { t } = useI18n();
  const idListy = useId();
  const [lista, setLista] = useState<Lista | null>(null);

  const pokaz = (tekst: string, kursor: number, wymus: boolean): void => {
    const el = poleRef.current;
    const p = podpowiedzi(tekst, kursor, tabele, { wymus });
    if (el === null || p === null) {
      setLista(null);
      return;
    }
    const { x, y } = polozenieKursora(el, kursor);
    setLista({ od: p.od, do: p.do, pozycje: p.lista, wybrana: 0, x: Math.max(0, Math.min(x, el.clientWidth - SZEROKOSC_LISTY)), y });
  };

  const przyjmij = (p: Podpowiedz): void => {
    if (lista === null) return;
    /** Wpisane `[Ite` dostaje domknięcie — `[Items]`, a nie `[Items` do poprawiania ręką. */
    const domknij = value[lista.od - 1] === '[' && value[lista.do] !== ']';
    const wstawka = domknij ? `${p.tekst}]` : p.tekst;
    const kursor = lista.od + wstawka.length;
    onChange(value.slice(0, lista.od) + wstawka + value.slice(lista.do));
    setLista(null);
    requestAnimationFrame(() => {
      const el = poleRef.current;
      if (el === null) return;
      el.focus();
      el.setSelectionRange(kursor, kursor);
    });
  };

  return (
    <div className="relative">
      <textarea
        ref={poleRef}
        value={value}
        spellCheck={false}
        rows={6}
        placeholder={placeholder}
        aria-autocomplete="list"
        aria-controls={lista !== null ? idListy : undefined}
        aria-activedescendant={lista !== null ? `${idListy}-${lista.wybrana}` : undefined}
        onChange={(e) => {
          onChange(e.target.value);
          pokaz(e.target.value, e.target.selectionStart, false);
        }}
        onBlur={() => setLista(null)}
        onClick={() => setLista(null)}
        onKeyDown={(e) => {
          if (lista !== null) {
            const n = lista.pozycje.length;
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
              e.preventDefault();
              setLista({ ...lista, wybrana: (lista.wybrana + (e.key === 'ArrowDown' ? 1 : n - 1)) % n });
              return;
            }
            if (e.key === 'Tab' || (e.key === 'Enter' && !e.ctrlKey && !e.metaKey && !e.shiftKey)) {
              e.preventDefault();
              przyjmij(lista.pozycje[lista.wybrana]!);
              return;
            }
            if (e.key === 'Escape') {
              e.preventDefault();
              setLista(null);
              return;
            }
            if (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'Home' || e.key === 'End') setLista(null);
          }
          /** Jak IntelliSense w SSMS: Ctrl+Spacja otwiera listę także bez wpisanej litery. */
          if (e.key === ' ' && e.ctrlKey) {
            e.preventDefault();
            pokaz(value, e.currentTarget.selectionStart, true);
            return;
          }
          /** Jak w SSMS: F5 albo Ctrl+Enter wykonuje. */
          if (e.key === 'F5' || (e.key === 'Enter' && (e.ctrlKey || e.metaKey))) {
            e.preventDefault();
            setLista(null);
            onWykonaj();
          }
        }}
        className="w-full resize-y rounded-md border border-border bg-background p-2 font-mono text-xs leading-5 outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
      />
      {/* ⚠️ Pozycja łapie `mousedown`, nie `click`: kliknięcie zabrałoby fokus polu, a `blur` zamyka listę. */}
      {lista !== null && (
        <ul
          id={idListy}
          role="listbox"
          aria-label={t('baza.podpowiedzi')}
          style={{ left: lista.x, top: lista.y }}
          className="absolute z-20 max-h-56 w-72 overflow-y-auto rounded-md border border-border bg-[var(--popover)] p-1 text-xs text-[var(--popover-foreground)] shadow-md"
        >
          {lista.pozycje.map((p, i) => (
            <li
              key={`${p.rodzaj}-${p.tekst}`}
              id={`${idListy}-${i}`}
              role="option"
              aria-selected={i === lista.wybrana}
              onMouseDown={(e) => {
                e.preventDefault();
                przyjmij(p);
              }}
              className={cn('flex cursor-pointer items-center gap-2 rounded px-1.5 py-0.5', i === lista.wybrana && 'bg-accent')}
            >
              <span className="truncate font-mono">{p.tekst}</span>
              <span className="ml-auto shrink-0 truncate text-[11px] text-muted-foreground">
                {p.rodzaj === 'KOLUMNA' ? p.opis : t(p.rodzaj === 'WIDOK' ? 'baza.widok' : 'baza.tabela')}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
